import React, { FC, useCallback, useMemo } from 'react';
import { ColumnProps } from 'antd/lib/table';
import { useTranslation } from 'react-i18next';
import { Table, notification, confirm, useMe, UserName, Skeleton, PageHeading } from 'src/common';
import styles from './index.module.scss';
import { useSendInviteToCompany, useCompanyEmployees } from 'src/models/company/Users/hooks';
import { MfaMode } from 'src/models/users/types';
import { trackUserInviteByCompanyAdmin } from 'src/segment/events';
import modalStyles from 'src/common/Modal/index.module.scss';
import EmployeeStatus, { EmployeeStatusBadge } from './EmployeeStatus';
import { faUsers } from '@fortawesome/pro-light-svg-icons';
import { ICompanyUser, EmployeesColumnsName } from 'src/models/company/Users/types';
import classNames from 'classnames';
import InviteUsers from './InviteUsers';
import { QuickActions, QuickActionsItem } from 'src/shared/QuickActions';
import { FeatureFlag, useFeatureFlag } from 'src/helpers/featureFlag';
import { Ownerships, ownership } from 'src/models/ownership';

function useTableColumns(
  hideFields: readonly EmployeesColumnsName[],
  onReInvite: (record: ICompanyUser) => void,
  checkEditable: (email: string) => boolean
): ColumnProps<ICompanyUser>[] {
  const { t } = useTranslation();

  return useMemo(() => {
    const columns: ColumnProps<ICompanyUser>[] = [
      {
        title: t('Common.name'),
        dataIndex: 'userName',
        className: styles.colName,
        ellipsis: true,
        render: (_, record) => {
          return (
            <div className={styles.user}>
              <UserName mode={'full'} user={record} size={'l'} />
            </div>
          );
        }
      },
      {
        title: t('Common.status'),
        dataIndex: 'status',
        className: styles.colStatus,
        render: function RenderActiveStatus(_, record) {
          const { enabled, email } = record;
          return (
            <div className={styles.wrapStatus}>
              {checkEditable(email) ? (
                <EmployeeStatus enabled={enabled} email={email} />
              ) : (
                <EmployeeStatusBadge enabled={enabled} />
              )}
            </div>
          );
        }
      },
      {
        title: t('Teammates.role'),
        dataIndex: 'role',
        className: styles.colRole,
        render(_, record) {
          const roleKey = record.isAdmin ? 'Teammates.adminRole' : 'Teammates.userRole';
          return <>{t(roleKey)}</>;
        }
      },
      {
        title: '2FA',
        dataIndex: 'mfaMode',
        className: styles.colMfa,
        render: mfaMode => <>{t(mfaMode === MfaMode.TOTP ? 'Common.enabled' : 'Common.disabled')}</>
      },
      {
        title: t('Teammates.joined'),
        dataIndex: 'createdOn',
        className: styles.colJoinedDate,
        render: createdOn => <>{createdOn}</>
      },
      {
        title: '',
        className: styles.colQuickActions,
        dataIndex: 'quickActions',
        render: (_, record) => {
          return (
            <>
              {checkEditable(record.email) && (
                <QuickActions>
                  <QuickActionsItem
                    key={'reInvite'}
                    onClick={() => onReInvite(record)}
                    title={t('Teammates.resendInvite')}
                  />
                </QuickActions>
              )}
            </>
          );
        }
      }
    ];
    return columns.filter(column => !hideFields.some(_ => column.dataIndex === _));
  }, [t, checkEditable, onReInvite, hideFields]);
}

export const Employees: FC = () => {
  const { t } = useTranslation();
  const { data: meData } = useMe();
  const isAdmin = !!meData?.isAdmin;
  const companyName = meData?.company.companyName;
  const { data: company, loading: isLoading, refetch } = useCompanyEmployees();
  const checkEditable = useCallback(
    (email: string) =>
      ownership.name !== Ownerships.Clira && !!company?.createdBy && email !== company.createdBy.email && isAdmin,
    [company, isAdmin]
  );

  const dataPendingUsers = useMemo((): ICompanyUser[] => {
    return [];
  }, []);

  const dataActiveUsers = useMemo((): ICompanyUser[] => {
    if (!company) {
      return [];
    }
    const { createdBy, personel } = company;

    return createdBy ? [createdBy, ...personel] : personel;
  }, [company]);

  const [sendInvite] = useSendInviteToCompany();

  const onReInvite = useCallback(
    (record: ICompanyUser) => {
      confirm.confirm({
        title: t('Teammates.inviteConfirmText'),
        content: t('Teammates.inviteConfirmDescription'),
        okText: t('Common.yes'),
        cancelText: t('Common.no'),
        className: modalStyles.confirmModal,
        centered: true,
        onOk() {
          const { email, name, lastName, role } = record;
          sendInvite({ variables: { email } })
            .then(() => {
              trackUserInviteByCompanyAdmin({
                email,
                name: name || '',
                lastName: lastName || '',
                role,
                invitedByRole: String(meData?.company?.pricePlan)
              });
              refetch();
            })
            .catch(() => {
              notification.error({
                message: t('Teammates.employeeHasNotAdded'),
                description: t('Common.unknownErrorDesc')
              });
            });
        }
      });
    },
    [t, sendInvite, meData, refetch]
  );

  const hiddenColumnsPending = [
    EmployeesColumnsName.mfaMode,
    EmployeesColumnsName.role,
    EmployeesColumnsName.status
  ] as const;
  const hiddenColumnsActive = [] as const;

  const columnsPendingUsers: ColumnProps<ICompanyUser>[] = useTableColumns(
    hiddenColumnsPending,
    onReInvite,
    checkEditable
  );
  const columnsActiveUsers: ColumnProps<ICompanyUser>[] = useTableColumns(
    hiddenColumnsActive,
    onReInvite,
    checkEditable
  );

  const inviteTeammatesFeature = useFeatureFlag(FeatureFlag.InviteTeammates);

  return (
    <>
      <PageHeading
        title={t('Navigation.Settings.teammates')}
        desc={t('Teammates.teammatesSettingsDesc', { companyName: companyName ?? t('Company.company') })}
        icon={faUsers}
        actions={isAdmin && inviteTeammatesFeature && <InviteUsers />}
      />
      {isLoading ? (
        <Skeleton active loading />
      ) : (
        <>
          {!!dataPendingUsers.length && (
            <div className={styles.contentInner}>
              <h3 className={styles.contentTitle}>
                <span>{t('Teammates.pendingUsersTitle', { count: dataPendingUsers.length })}</span>
              </h3>
              <Table
                rowKey={'email'}
                columns={columnsPendingUsers}
                dataSource={dataPendingUsers}
                className={classNames(styles.table, styles.pendingTable)}
                size={'large'}
              />
            </div>
          )}
          <div className={styles.contentInner}>
            <h3 className={styles.contentTitle}>
              <span>{t('Teammates.activeUsersTitle', { count: dataActiveUsers.length })}</span>
            </h3>
            <Table
              rowKey={'email'}
              columns={columnsActiveUsers}
              dataSource={dataActiveUsers}
              className={styles.table}
              size={'large'}
            />
          </div>
        </>
      )}
    </>
  );
};

export default Employees;
